/**
 * CR431: Unified Deal Validity Helper
 * 
 * Single source of truth for deal validity display, status computation,
 * and date resolution. All V2 surfaces MUST use this helper instead of
 * reading date fields directly.
 * 
 * Resolves dates from multiple sources in priority order:
 * 1. valid_from_date / valid_until_date (date-only, timezone-safe)
 * 2. dates.valid_from / dates.valid_until (canonical_deal_json)
 * 3. valid_from / valid_until (top-level DB columns)
 * 4. start_date / end_date (legacy)
 * 
 * RULE: UI renders "valid dates" ONLY from resolved validity fields.
 * Flyer/source window dates are NEVER displayed as deal validity.
 */

import { toDateOnlyString, parseDateOnlyToLocal } from "@/lib/dates/dateOnly";
import { formatEligibilityRange } from "@/lib/deals/formatEligibilityRange";

// =============================================================================
// TYPES
// =============================================================================

export type ValidityStatus = "known" | "partial" | "unknown" | "inferred_source_window";
export type ValidityTimeStatus = "active" | "upcoming" | "expired" | "unknown";

export interface DealValidity {
  /** Resolved valid-from date (YYYY-MM-DD) or null */
  validFrom: string | null;
  /** Resolved valid-to date (YYYY-MM-DD) or null */
  validTo: string | null;
  /** Whether dates are known, partial, or unknown */
  status: ValidityStatus;
  /** Where the dates came from */
  source: string | null;
  /** Confidence score (0-1) if available */
  confidence: number | null;
}

// =============================================================================
// MAIN HELPERS
// =============================================================================

/**
 * Pick the first non-null date-only string from a list of candidates.
 * Each candidate is run through toDateOnlyString for safe normalization.
 */
function pickDateOnly(...vals: Array<any>): string | null {
  for (const v of vals) {
    const s = toDateOnlyString(v);
    if (s) return s;
  }
  return null;
}

/**
 * Resolve deal validity from any deal-like object.
 * CR454: Checks multiple field sources in priority order, preferring date-only
 * fields first (timezone-safe), then falling back to timestamps.
 */
export function getDealValidity(deal: Record<string, any>): DealValidity {
  const canon = deal.canonical_deal_json ?? null;
  const nestedDates = deal.dates ?? null;

  // CR454: Resolution order — prefer date-only fields, then timestamps
  // 1. start_date / end_date (often date-only from views)
  // 2. valid_from_date / valid_until_date (explicit date-only columns)
  // 3. dates.valid_from / dates.valid_until (nested object)
  // 4. canonical_deal_json.dates.valid_from / valid_until
  // 5. valid_from / valid_until (timestamps → normalized to date-only)
  const validFrom = pickDateOnly(
    deal.start_date,
    deal.valid_from_date,
    nestedDates?.valid_from,
    canon?.dates?.valid_from,
    deal.valid_from, // timestamp fallback
  );

  const validTo = pickDateOnly(
    deal.end_date,
    deal.valid_until_date,
    nestedDates?.valid_until,
    canon?.dates?.valid_until,
    deal.valid_until, // timestamp fallback
  );

  // Resolve status
  const explicitStatus = deal.validity_status as ValidityStatus | undefined;
  let status: ValidityStatus;
  if (explicitStatus && ["known", "partial", "unknown", "inferred_source_window"].includes(explicitStatus)) {
    status = explicitStatus;
  } else if (validFrom && validTo) {
    status = "known";
  } else if (validFrom || validTo) {
    status = "partial";
  } else {
    status = "unknown";
  }

  return {
    validFrom,
    validTo,
    status,
    source: deal.validity_source ?? null,
    confidence: deal.validity_confidence ?? null,
  };
}

/**
 * Format deal validity as a human-readable label.
 * Returns range string for known dates, or "Valid dates unknown" otherwise.
 */
export function getDealValidityLabel(deal: Record<string, any>): string {
  const v = getDealValidity(deal);

  if (v.validFrom || v.validTo) {
    const formatted = formatEligibilityRange(v.validFrom, v.validTo);
    if (formatted) return formatted;
  }

  // Check for raw text fallback
  const rawText = deal.dates?.dates_text_raw ?? deal.validity_raw_text ?? null;
  if (rawText && typeof rawText === "string" && rawText.trim()) {
    return rawText.trim();
  }

  return "Valid dates unknown";
}

/**
 * Compute deal time status (active/upcoming/expired/unknown).
 * Uses Toronto timezone for date comparison.
 * 
 * Rule: Missing end_date with known start_date → active.
 * Rule: No dates at all → unknown.
 */
export function getDealTimeStatus(
  deal: Record<string, any>,
  asOfDate?: Date
): ValidityTimeStatus {
  const v = getDealValidity(deal);

  if (!v.validFrom && !v.validTo) {
    return "unknown";
  }

  const now = asOfDate || new Date();
  const today = parseDateOnlyToLocal(
    now.toLocaleDateString("en-CA", { timeZone: "America/Toronto" })
  );

  const start = v.validFrom ? parseDateOnlyToLocal(v.validFrom) : null;
  const end = v.validTo ? parseDateOnlyToLocal(v.validTo) : null;

  // Expired: end date is in the past
  if (end && end < today) {
    return "expired";
  }

  // Upcoming: start date is in the future
  if (start && start > today) {
    return "upcoming";
  }

  // Active: within range OR missing end_date
  return "active";
}
